import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { useCallback } from 'react';
import { z } from 'zod';

import { getCoachAvailabilitiesForCoach, getCoachClasses, getUser } from '@/lib/api-calls.js';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling.js';
import { QueryKeys } from '@/lib/queries.js';

import { InfoPage } from '@/components/info-page.js';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary.js';

import { FrequentTimeZones, isAirAdmin } from '@airreading/common';
import { ClassesCard } from '../-components/classes-card.js';
import { ManagePermissionsDropdown } from '../-components/manage-permissions-dropdown.js';
import { coachesTabs } from '../-components/tabs.js';
import { TeachingAvailabilityCard } from '../-components/teaching-availability-card.js';
import { validateRouteRole } from '@/lib/routing.js';
import {
  OneTimeStudentGroupsCard,
  ReassignedStudentGroupsCard,
  StudentGroupsCardPagination,
} from '../-components/reassigned-student-groups-card.js';
import { pageSizeSchemaWithMax, paginationRequestSchema } from '@/lib/pagination.js';
import { getCurrentRegularStudentGroups } from '../-components/teacher-schedule-groups.js';

const scheduleSearchSchema = paginationRequestSchema.extend({
  currentPageIndex: z.number().int().nonnegative().catch(0),
  currentPageSize: pageSizeSchemaWithMax(),
  oneTimePageIndex: z.number().int().nonnegative().catch(0),
  oneTimePageSize: pageSizeSchemaWithMax(),
});
type ScheduleSearch = z.infer<typeof scheduleSearchSchema>;

export const Route = createFileRoute('/_layout/teachers/$teacherId/schedule')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SchedulePage,
  validateSearch: scheduleSearchSchema,
});

/** Each table on this page keeps its own pagination in the URL under its own pair of search keys. */
function useSearchPagination(
  pageIndexKey: keyof ScheduleSearch,
  pageSizeKey: keyof ScheduleSearch,
): StudentGroupsCardPagination {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const pageIndex = search[pageIndexKey];
  const pageSize = search[pageSizeKey];

  const onPaginationChange = useCallback<OnChangeFn<PaginationState>>(
    (updaterOrNewState) => {
      const next =
        typeof updaterOrNewState === 'function' ? updaterOrNewState({ pageIndex, pageSize }) : updaterOrNewState;
      navigate({
        search: (prev) => ({ ...prev, [pageIndexKey]: next.pageIndex, [pageSizeKey]: next.pageSize }),
      });
    },
    [navigate, pageIndex, pageIndexKey, pageSize, pageSizeKey],
  );

  return { pageIndex, pageSize, onPaginationChange };
}

function SchedulePage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load teacher. Please try again.',
        };
      }}
    >
      <ScheduleDetail />
    </InfoPageSuspenseBoundary>
  );
}

function ScheduleDetail() {
  const { teacherId: userId } = Route.useParams();
  const { data: user } = useSuspenseQuery({
    queryKey: QueryKeys.getUser(userId),
    queryFn: () => getUser(userId),
  });

  const { data: availabilities } = useSuspenseQuery({
    ...getCoachAvailabilitiesForCoach(userId),
  });

  // Groups this teacher was removed from are still current until their last remaining class.
  const { data: classes } = useSuspenseQuery({
    ...getCoachClasses(userId, true),
  });

  const currentRegularStudentGroups = getCurrentRegularStudentGroups(classes);

  const pastPagination = useSearchPagination('pageIndex', 'pageSize');
  const currentPagination = useSearchPagination('currentPageIndex', 'currentPageSize');
  const oneTimePagination = useSearchPagination('oneTimePageIndex', 'oneTimePageSize');

  return (
    <InfoPage
      headerText={user.fullNameOrEmail}
      headerActions={<ManagePermissionsDropdown user={user} />}
      tabs={coachesTabs({ teacherId: userId })}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Teachers',
            link: {
              to: '/teachers',
            },
          },
        ],
        currentPage: user.fullNameOrEmail ?? '',
      }}
    >
      <TeachingAvailabilityCard availabilities={availabilities} timeZone={FrequentTimeZones.UsCentral} />
      <ClassesCard classes={currentRegularStudentGroups} pagination={currentPagination} />
      <OneTimeStudentGroupsCard coachId={userId} pagination={oneTimePagination} />
      <ReassignedStudentGroupsCard coachId={userId} pagination={pastPagination} />
    </InfoPage>
  );
}
